/*
    https://www.chakra-ui.com/docs/components/segmented-control
    Chakra UI v3 has one of these, but since we are using v2, I created a custom one.
    Could be useful in other parts of the codebase at some point.
*/

import React from "react"
import {
  Tabs,
  TabList,
  Tab,
  useTab,
  TabProps,
} from "@chakra-ui/react"

type Option = {
  label: React.ReactNode
  value: string
}

type SegmentedControlProps = {
  options: Option[]
  value: string
  onChange: (value: string) => void
  size?: "default" | "sm"
  // Allow other Chakra Tabs props
  [x: string]: any
}

// Custom Tab that looks like a segment
type SegmentedTabProps = {
  size?: "default" | "sm"
} & TabProps

// Custom Tab that looks like a segment
const SegmentedTab = ({ size = "default", children, ...rest }: SegmentedTabProps) => {
  const tabProps = useTab({})
  const isSelected = !!tabProps["aria-selected"]

  // Adjust styles based on size
  const paddingX = size === "sm" ? 3 : 4
  const paddingY = size === "sm" ? 1 : 2
  const fontSize = size === "sm" ? "sm" : "sm"

  return (
    <Tab
      {...tabProps}
      {...rest}
      px={paddingX}
      py={paddingY}
      fontWeight="medium"
      fontSize={fontSize}
      color={isSelected ? "gray.900" : "gray.500"}
      _selected={{
        bg: "gray.100",
      }}
      _hover={{
        bg: "gray.100",
      }}
      borderColor={isSelected ? "gray.300" : "transparent"}
      borderRadius="md"
      borderWidth={1}
    >
      {children}
    </Tab>
  )
}

// Main component
export const SegmentedControl = ({
  options,
  value,
  onChange,
  size = "default",
  ...props
}: SegmentedControlProps) => {
  const index = options.findIndex((opt) => opt.value === value)

  return (
    <Tabs
      {...props}
      index={index}
      onChange={(i) => onChange(options[i].value)}
      variant="unstyled"
      width={props.width ?? "fit-content"}
    >
      <TabList
        bg="white"
        p="1"
        borderRadius={size === "sm" ? "lg" : "xl"}
        borderWidth={1}
        borderColor="gray.300"
        gap={1}
        w="full"
      >
        {options.map((opt) => (
          <SegmentedTab key={String(opt.value)} size={size} flex={props.width === "full" ? 1 : undefined}>
            {opt.label}
          </SegmentedTab>
        ))}
      </TabList>

    </Tabs>
  )
}

export default SegmentedControl
